import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { Config } from "@config";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import autobind from "autobind-decorator";
import { Alert } from "bm-ui-kit";
import type { ReactNode } from "react";
import { Button, Modal } from "react-bootstrap";
import { InView } from "react-intersection-observer";

interface Props extends MeasureComponentProps {
	type: string;
}

interface State extends MeasureComponentState {
	showModal: boolean;
}

/**
 * @Shortcode
 *
 * @title "Article offer"
 * @shortcode "article_offer"
 * @type "widget"
 * @propsList "[{"type":"BUILDING_SOCIETY"},{"type":"CREDIT_CARD"},{"type":"CSOK"},{"type":"DEPOSIT"},{"type":"FACTORING"},{"type":"FGS"},{"type":"GOVERNMENT_SECURITY"},{"type":"MORTGAGE"},{"type":"MUTUAL_FUND"},{"type":"PENSION"},{"type":"RETAIL_ACCOUNT"},{"type":"SME_ACCOUNT"},{"type":"SME_LOAN"},{"type":"SZECHENYI"},{"type":"HOW_MUCH_CREDIT"},{"type":"CREDIT_WORTHY"}]"
 * @lazyHydrate "whenIdle"
 */
export default class ArticleOffer extends MeasureComponent<Props, State> {
	protected productTypes = "";
	protected readonly componentType: EventComponentType = "widget";

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			showModal: false,
		};
	}

	public render(): ReactNode {
		const { type } = this.props;
		const { wasVisible } = this.state;

		const data = Config.getProductData[type];

		return (
			<InView {...GoogleAnalytics4.getInViewProps()} skip={wasVisible} onChange={this.onVisibilityChange}>
				<Alert icon="auto" variant="info" size="sm">
					A Bankmonitor.hu{" "}
					<Button onClick={() => this.toggleModal()} className="p-0 align-baseline" variant="link">
						ingyenes szolgáltatásokat
					</Button>{" "}
					nyújtó weboldal: több, mint 30 bank és pénzügyi szolgáltató több ezer termékének objektív
					összehasonlítása, szakértői írások a jó és megalapozott pénzügyi döntésekért.{" "}
					{data && (
						<>
							Tájékoztatáson túl konkrét támogatást is nyújtunk a kiválasztott banki termék szerződésének
							megkötésében. Jelen írásunkhoz hasznos kiegészítést adhat számodra a{" "}
							<a href={data.calculatorUrl} target="_blank" rel="noreferrer">
								{data.name} kalkulátorunk
							</a>
							.
						</>
					)}
				</Alert>
				{this.renderFreeServiceModal()}
			</InView>
		);
	}

	@autobind
	private toggleModal(): void {
		const { showModal } = this.state;
		this.setState({
			showModal: !showModal,
		});
	}

	@autobind
	protected onModalHide(): void {
		this.setState({
			showModal: false,
		});
	}

	private renderFreeServiceModal(): ReactNode {
		const { showModal } = this.state;

		return (
			<Modal show={showModal} onHide={this.onModalHide} centered>
				<Modal.Header closeButton>
					<Modal.Title>Miért lehet ingyenes a Bankmonitor szolgáltatása?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					A Bankmonitor bevételei (jelentős részben) a bankoktól származik: számos olyan feladatot elvégzünk,
					ami eredetileg egy bankfiók tevékenységéhez tartozik. Így költséget takarítunk meg a bankoknak az
					ügyfélkiszolgálásunkkal és ebből a költségmegtakarításból részesedünk. Számunkra mindegy, hogy mely
					bank, mely termékét választja az ügyfelünk: a legfontosabb, hogy hozzá tudjuk segíteni a jó
					döntéshez.
				</Modal.Body>
			</Modal>
		);
	}

	protected getComponentName(): string {
		return "ArticleOffer";
	}
}
